import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Linkedin, Mail } from "lucide-react";
import { CATEGORIES } from "../../data/products";

const FOOTER_COLS = [
  {
    title: "Shop",
    links: CATEGORIES.map((c) => ({ label: c.name, slug: c.slug })),
  },
  {
    title: "Company",
    links: [
      { label: "About us", to: "/about" },
      { label: "Why wholesale", to: "/why-wholesale" },
      { label: "Quality & sourcing", to: "/quality" },
      { label: "Press & media", to: "/press" },
      { label: "Careers", to: "/careers" },
    ],
  },
  {
    title: "Customer",
    links: [
      { label: "Track order", href: "#tracking" },
      { label: "Shipping policy", to: "/shipping-policy" },
      { label: "Returns & refunds", to: "/returns" },
      { label: "Cold-chain guarantee", to: "/cold-chain" },
      { label: "Help center", to: "/help" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy policy", to: "/privacy" },
      { label: "Terms of service", to: "/terms" },
      { label: "Cookie settings", to: "/cookies" },
      { label: "Verified buyer program", to: "/verified-buyer" },
      { label: "Counterfeit reporting", to: "/counterfeit" },
    ],
  },
];

export const Footer = () => {
  const scrollToCategory = (slug) => {
    const el = document.getElementById(`cat-${slug}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer className="bg-black text-white" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 pt-24 md:pt-32 pb-12">
        {/* Trustpilot strip */}
        <div className="flex items-center justify-between gap-4 pb-12 mb-16 border-b border-neutral-800 flex-wrap" data-testid="trustpilot-strip">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-[#00b67a] text-white px-3 py-1.5 text-sm font-bold tracking-tight">
              <span>★</span>
              <span>Trustpilot</span>
            </div>
            <div className="flex items-center gap-0.5 text-[#00b67a]">
              {[1,2,3,4,5].map((i) => (
                <span key={i} className="bg-[#00b67a] text-white w-6 h-6 flex items-center justify-center text-base">★</span>
              ))}
            </div>
            <div className="text-sm">
              <span className="font-bold">Excellent</span>
              <span className="text-neutral-400 ml-2">5.0 · Based on 1,247 clinic reviews</span>
            </div>
          </div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-400">Verified by clinicians worldwide</div>
        </div>

        {/* Top — newsletter / brand */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20 pb-16 border-b border-neutral-800">
          <div className="md:col-span-6">
            <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tight">
              <span className="block font-black">Stocked in Seoul.</span>
              <span className="block font-thin">Shipped worldwide.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex flex-col justify-end gap-4">
            <p className="text-sm text-neutral-400 font-light">
              Subscribe for restocks, new arrivals and wholesale-only pricing drops.
            </p>
            <div className="flex border border-neutral-700">
              <input
                type="email"
                placeholder="your-clinic@email.com"
                className="flex-1 bg-transparent px-5 py-4 text-sm placeholder:text-neutral-500 focus:outline-none"
                data-testid="footer-newsletter-input"
              />
              <button
                className="bg-white text-black px-6 hover:bg-neutral-200 transition-colors flex items-center gap-2 text-sm font-medium"
                data-testid="footer-newsletter-submit"
              >
                Subscribe <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-20">
          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-5 font-medium">
                {col.title}
              </div>
              <ul className="space-y-3">
                {col.links.map((l) => {
                  if (l.slug) {
                    return (
                      <li key={l.slug}>
                        <button
                          onClick={() => scrollToCategory(l.slug)}
                          className="text-sm text-neutral-300 hover:text-white transition-colors text-left"
                          data-testid={`footer-link-${l.slug}`}
                        >
                          {l.label}
                        </button>
                      </li>
                    );
                  }
                  if (l.to) {
                    return (
                      <li key={l.to}>
                        <Link
                          to={l.to}
                          className="text-sm text-neutral-300 hover:text-white transition-colors"
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {l.label}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-sm text-neutral-300 hover:text-white transition-colors"
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 border-t border-neutral-800">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-xl font-thin">Discount</span>
            <span className="font-heading text-xl font-black">Fillers</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="#" className="w-9 h-9 border border-neutral-700 hover:bg-white hover:text-black transition-colors flex items-center justify-center" aria-label="Instagram" data-testid="social-instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="#" className="w-9 h-9 border border-neutral-700 hover:bg-white hover:text-black transition-colors flex items-center justify-center" aria-label="LinkedIn" data-testid="social-linkedin">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href="mailto:hello@wholesalefillers.world" className="w-9 h-9 border border-neutral-700 hover:bg-white hover:text-black transition-colors flex items-center justify-center" aria-label="Email" data-testid="social-email">
              <Mail className="w-4 h-4" />
            </a>
          </div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500">
            © {new Date().getFullYear()} Discount Fillers · All rights reserved
          </div>
        </div>
      </div>
    </footer>
  );
};
